/**
 * Field check-in pill in the top header.
 *
 * - When no active check-in: shows "Check in" button → opens CheckInModal.
 * - When checked in: shows live elapsed time + location + "Check out" button.
 * - Polls every 60s so multiple tabs stay in sync.
 *
 * Lives in the desktop sidebar header (also visible on mobile via top bar).
 */
import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { MapPin, LogIn, LogOut, Loader2, Crosshair, X, Link2, Search } from "lucide-react";
import { toast } from "sonner";

// All 100 North Carolina counties — used as a free-typeable datalist in
// the check-in modal so staff can either pick from the list OR type a
// custom value (some outreach happens just over state lines / on tribal
// lands that don't map to a county).
const NC_COUNTIES = [
  "Alamance","Alexander","Alleghany","Anson","Ashe","Avery","Beaufort","Bertie","Bladen","Brunswick",
  "Buncombe","Burke","Cabarrus","Caldwell","Camden","Carteret","Caswell","Catawba","Chatham","Cherokee",
  "Chowan","Clay","Cleveland","Columbus","Craven","Cumberland","Currituck","Dare","Davidson","Davie",
  "Duplin","Durham","Edgecombe","Forsyth","Franklin","Gaston","Gates","Graham","Granville","Greene",
  "Guilford","Halifax","Harnett","Haywood","Henderson","Hertford","Hoke","Hyde","Iredell","Jackson",
  "Johnston","Jones","Lee","Lenoir","Lincoln","Macon","Madison","Martin","McDowell","Mecklenburg",
  "Mitchell","Montgomery","Moore","Nash","New Hanover","Northampton","Onslow","Orange","Pamlico","Pasquotank",
  "Pender","Perquimans","Person","Pitt","Polk","Randolph","Richmond","Robeson","Rockingham","Rowan",
  "Rutherford","Sampson","Scotland","Stanly","Stokes","Surry","Swain","Transylvania","Tyrrell","Union",
  "Vance","Wake","Warren","Washington","Watauga","Wayne","Wilkes","Wilson","Yadkin","Yancey",
];

export default function CheckInPill() {
  const [active, setActive] = useState(undefined); // undefined while loading, null when none
  const [modalOpen, setModalOpen] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/field/check-in/active");
      setActive(data || null);
    } catch {
      setActive(null);
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 60_000);
    return () => clearInterval(id);
  }, [refresh]);

  const checkOut = async () => {
    if (!active) return;
    if (!window.confirm("End this check-in?")) return;
    setCheckingOut(true);
    try {
      await api.post(`/field/check-in/${active.id}/check-out`, { note: null });
      toast.success("Checked out");
      setActive(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCheckingOut(false);
    }
  };

  if (active === undefined) return null; // not yet loaded

  return (
    <>
      {active ? (
        <div
          data-testid="checkin-pill-active"
          className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E8ECE7] border border-[#7E937D]"
        >
          <MapPin className="w-3.5 h-3.5 text-[#166534] flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-[#166534] leading-none">
              Checked in
            </div>
            <div className="text-xs text-[#0F172A] font-medium truncate max-w-[180px]" title={locationLabel(active)}>
              {locationLabel(active)} · <ElapsedSince startIso={active.check_in_at} />
            </div>
          </div>
          <button
            type="button"
            onClick={checkOut}
            disabled={checkingOut}
            data-testid="checkin-pill-checkout-btn"
            className="text-xs text-[#991B1B] hover:underline font-medium flex-shrink-0"
          >
            {checkingOut ? "…" : "Check out"}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          data-testid="checkin-pill-open-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1E3A8A] text-white text-xs font-medium hover:bg-[#172554]"
        >
          <LogIn className="w-3.5 h-3.5" />
          Check in
        </button>
      )}
      {modalOpen && (
        <CheckInModal
          onClose={() => setModalOpen(false)}
          onDone={() => { setModalOpen(false); refresh(); }}
        />
      )}
    </>
  );
}

/* ============================================================ */
/* Check-in modal                                                */
/* ============================================================ */

function CheckInModal({ onClose, onDone }) {
  const [geoStatus, setGeoStatus] = useState("idle"); // idle | requesting | granted | denied | error
  const [coords, setCoords] = useState(null);
  const [manualLabel, setManualLabel] = useState("");
  const [county, setCounty] = useState("");
  const [note, setNote] = useState("");
  const [linkedType, setLinkedType] = useState("");
  const [linkedId, setLinkedId] = useState("");
  const [linkedLabel, setLinkedLabel] = useState(""); // human-readable summary of the current pick
  const [outreachEvents, setOutreachEvents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [csQuery, setCsQuery] = useState("");
  const [csResults, setCsResults] = useState([]);
  const [csSearching, setCsSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Try geo automatically on open. If denied or not supported, user can type a manual label.
  useEffect(() => {
    if (!("geolocation" in navigator)) {
      setGeoStatus("error");
      return;
    }
    setGeoStatus("requesting");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy_m: pos.coords.accuracy,
        });
        setGeoStatus("granted");
      },
      (e) => {
        setGeoStatus(e.code === e.PERMISSION_DENIED ? "denied" : "error");
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60_000 },
    );
  }, []);

  // Load upcoming outreach events + my open tasks for the optional "link to" select.
  useEffect(() => {
    api.get("/outreach")
      .then(({ data }) => setOutreachEvents(Array.isArray(data) ? data.slice(0, 30) : []))
      .catch(() => setOutreachEvents([]));
    api.get("/tasks", { params: { status: "open", mine_only: true } })
      .then(({ data }) => setTasks(Array.isArray(data) ? data.slice(0, 30) : []))
      .catch(() => setTasks([]));
  }, []);

  // Debounced care-seeker search for the linked-care-seeker picker.
  useEffect(() => {
    if (linkedType !== "care_seeker") return;
    if (!csQuery || csQuery.trim().length < 2) { setCsResults([]); return; }
    const t = setTimeout(async () => {
      setCsSearching(true);
      try {
        const { data } = await api.get("/care-seekers", { params: { q: csQuery.trim(), limit: 10 } });
        setCsResults(data || []);
      } catch {
        setCsResults([]);
      } finally {
        setCsSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [csQuery, linkedType]);

  const submit = async (e) => {
    e.preventDefault();
    if (!coords && !manualLabel.trim()) {
      setErr("Allow location access OR type a manual label (e.g., \"Pitt County Health Dept\").");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const payload = {
        latitude: coords?.latitude ?? null,
        longitude: coords?.longitude ?? null,
        accuracy_m: coords?.accuracy_m ?? null,
        manual_label: manualLabel.trim() || null,
        county: county || null,
        note: note.trim() || null,
        linked_type: linkedType || null,
        linked_id: linkedType && linkedId ? linkedId : null,
      };
      await api.post("/field/check-in", payload);
      toast.success("Checked in");
      onDone();
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[#0F172A]/40 flex items-center justify-center p-4"
      data-testid="checkin-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <form onSubmit={submit} className="bg-white rounded-2xl p-6 max-w-md w-full space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="hw-eyebrow mb-1">Field check-in</div>
            <h3 className="font-display text-xl font-semibold text-[#0F172A]">Where are you?</h3>
          </div>
          <button type="button" onClick={onClose}
            data-testid="checkin-modal-close"
            className="p-1 rounded-md text-[#334155] hover:bg-[#F9F8F6]">
            <X className="w-4 h-4" />
          </button>
        </div>

        <GeoStatusBanner status={geoStatus} coords={coords} />

        <div>
          <label className="block text-sm font-medium text-[#334155] mb-2">Location label (required if no GPS)</label>
          <input
            type="text"
            value={manualLabel}
            onChange={(e) => setManualLabel(e.target.value)}
            data-testid="checkin-manual-label"
            placeholder="e.g., Greene County Health Dept"
            className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0] focus:outline-none focus:ring-2 focus:ring-[#7E937D] text-[#0F172A]"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#334155] mb-2">County (optional)</label>
          <input
            type="text"
            list="checkin-nc-counties"
            value={county}
            onChange={(e) => setCounty(e.target.value)}
            data-testid="checkin-county"
            placeholder="Start typing — pick from 100 NC counties or enter your own"
            className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0] focus:outline-none focus:ring-2 focus:ring-[#7E937D] text-[#0F172A]"
            autoComplete="off"
          />
          <datalist id="checkin-nc-counties">
            {NC_COUNTIES.map((c) => <option key={c} value={c} />)}
          </datalist>
        </div>

        <div className="border border-[#E5E5E0] rounded-xl p-3 space-y-2">
          <div className="flex items-center gap-2 text-xs text-[#1E3A8A]">
            <Link2 className="w-3.5 h-3.5" /> Link this check-in (optional)
          </div>
          <select
            value={linkedType}
            onChange={(e) => { setLinkedType(e.target.value); setLinkedId(""); setLinkedLabel(""); setCsQuery(""); }}
            data-testid="checkin-linked-type"
            className="w-full px-3 py-2 rounded-lg bg-white border border-[#E5E5E0] text-sm"
          >
            <option value="">— Not linked —</option>
            <option value="outreach">An outreach event</option>
            <option value="care_seeker">A care-seeker visit</option>
            <option value="task">A task</option>
          </select>
          {linkedType === "outreach" && (
            <select
              value={linkedId}
              onChange={(e) => {
                setLinkedId(e.target.value);
                const o = outreachEvents.find((x) => x.id === e.target.value);
                setLinkedLabel(o ? `${o.title || o.activity_type} · ${o.activity_date}` : "");
              }}
              data-testid="checkin-linked-outreach"
              className="w-full px-3 py-2 rounded-lg bg-white border border-[#E5E5E0] text-sm"
            >
              <option value="">— Pick an event —</option>
              {outreachEvents.map((o) => (
                <option key={o.id} value={o.id}>{o.title || o.activity_type} · {o.activity_date}</option>
              ))}
            </select>
          )}
          {linkedType === "task" && (
            <select
              value={linkedId}
              onChange={(e) => {
                setLinkedId(e.target.value);
                const t = tasks.find((x) => x.id === e.target.value);
                setLinkedLabel(t ? (t.display_id ? `${t.display_id} · ${t.title}` : t.title) : "");
              }}
              data-testid="checkin-linked-task"
              className="w-full px-3 py-2 rounded-lg bg-white border border-[#E5E5E0] text-sm"
            >
              <option value="">— Pick a task —</option>
              {tasks.length === 0 && <option disabled>No open tasks</option>}
              {tasks.map((t) => (
                <option key={t.id} value={t.id}>{t.display_id ? `${t.display_id} · ` : ""}{t.title}</option>
              ))}
            </select>
          )}
          {linkedType === "care_seeker" && (
            <>
              {linkedId ? (
                <div className="px-3 py-2 rounded-lg bg-[#E8ECE7] border border-[#7E937D] flex items-center justify-between gap-2">
                  <div className="text-xs">
                    <div className="font-medium text-[#0F172A]">{linkedLabel}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setLinkedId(""); setLinkedLabel(""); setCsQuery(""); }}
                    data-testid="checkin-linked-clear"
                    className="text-[10px] text-[#1E3A8A] hover:underline"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#1E3A8A]" />
                    <input
                      type="search"
                      value={csQuery}
                      onChange={(e) => setCsQuery(e.target.value)}
                      data-testid="checkin-linked-care-seeker-search"
                      placeholder="Search by name or display ID"
                      className="w-full pl-8 pr-3 py-2 rounded-lg bg-white border border-[#E5E5E0] text-sm"
                    />
                  </div>
                  {csSearching ? (
                    <div className="text-xs text-[#1E3A8A]">Searching…</div>
                  ) : csResults.length > 0 ? (
                    <ul className="border border-[#E5E5E0] rounded-lg divide-y divide-[#E5E5E0] max-h-48 overflow-y-auto" data-testid="checkin-linked-care-seeker-results">
                      {csResults.map((cs) => (
                        <li key={cs.id}>
                          <button
                            type="button"
                            onClick={() => {
                              setLinkedId(cs.id);
                              setLinkedLabel(`${cs.preferred_name || cs.first_name} ${cs.last_name} · ${cs.display_id || ""}`);
                            }}
                            data-testid={`checkin-linked-care-seeker-result-${cs.id}`}
                            className="w-full text-left px-3 py-2 hover:bg-[#F9F8F6] flex items-center justify-between gap-2 text-xs"
                          >
                            <span className="font-medium text-[#0F172A]">{cs.preferred_name || cs.first_name} {cs.last_name}</span>
                            <span className="font-mono text-[10px] text-[#1E3A8A]">{cs.display_id}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : csQuery.trim().length >= 2 ? (
                    <div className="text-xs text-[#1E3A8A]">No matches.</div>
                  ) : null}
                </>
              )}
            </>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-[#334155] mb-2">Note (optional)</label>
          <textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            data-testid="checkin-note"
            placeholder="What are you doing here?"
            className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]"
          />
        </div>

        {err && <div data-testid="checkin-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose}
            data-testid="checkin-cancel"
            className="px-4 py-2 rounded-full border border-[#E5E5E0] text-[#334155] text-sm hover:bg-[#F9F8F6]">
            Cancel
          </button>
          <button type="submit" disabled={busy}
            data-testid="checkin-submit"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554] disabled:opacity-60">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            <LogIn className="w-3.5 h-3.5" />
            Check in
          </button>
        </div>
      </form>
    </div>
  );
}

const GeoStatusBanner = ({ status, coords }) => {
  if (status === "requesting") {
    return (
      <div className="text-xs text-[#1E3A8A] bg-[#E0E7FF] rounded-lg px-3 py-2 flex items-center gap-2" data-testid="checkin-geo-requesting">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Requesting location permission…
      </div>
    );
  }
  if (status === "granted" && coords) {
    return (
      <div className="text-xs text-[#166534] bg-[#E8ECE7] border border-[#7E937D] rounded-lg px-3 py-2 flex items-center gap-2" data-testid="checkin-geo-granted">
        <Crosshair className="w-3.5 h-3.5" />
        Location captured · accuracy ±{Math.round(coords.accuracy_m)}m
      </div>
    );
  }
  if (status === "denied") {
    return (
      <div className="text-xs text-[#92400E] bg-[#FEF3C7] border border-[#FCD34D] rounded-lg px-3 py-2" data-testid="checkin-geo-denied">
        Location permission denied. Type a manual label below — we&apos;ll fall back to an IP-based city for the audit log.
      </div>
    );
  }
  if (status === "error") {
    return (
      <div className="text-xs text-[#92400E] bg-[#FEF3C7] border border-[#FCD34D] rounded-lg px-3 py-2" data-testid="checkin-geo-error">
        Couldn&apos;t get a GPS fix. Type a manual label below.
      </div>
    );
  }
  return null;
};

/* ============================================================ */
/* Helpers                                                       */
/* ============================================================ */

function locationLabel(ci) {
  if (ci.manual_label) return ci.manual_label;
  if (ci.latitude && ci.longitude) {
    return `${ci.latitude.toFixed(3)}, ${ci.longitude.toFixed(3)}`;
  }
  if (ci.city) return `${ci.city}${ci.region ? `, ${ci.region}` : ""}`;
  return "Unknown location";
}

function ElapsedSince({ startIso }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);
  try {
    const start = new Date(startIso).getTime();
    const mins = Math.max(0, Math.round((now - start) / 60000));
    if (mins < 60) return <>{mins}m</>;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return <>{h}h {m}m</>;
  } catch {
    return null;
  }
}
